/**
 * Phone Service — "Test Like A Mobile" Interactive Session Client
 *
 * This service drives the phone-shaped TLAM modal by dispatching interactive
 * flow executions against a pre-locked modem. Phase 1 delivers voice dial + hangup
 * through `sys_flow_voice_call` / `sys_flow_voice_hangup`; SMS, browser, and voice
 * audio are scaffolded with explicit "not yet implemented" errors for UI clarity
 * until their supporting edge/infra work lands.
 *
 * Design (phase 1):
 * - No WebSocket. Commands go via HTTPS `executeFlow` with `interactiveLockId`.
 * - The backend validates the lock, runs FlowExecutor in-process (awaited),
 *   updates the execution doc, returns when done.
 * - Live status updates for long-running flows come from Firestore onSnapshot
 *   on `executions/{id}`, parsed into the same event shapes the TLAM modal
 *   already listens for.
 * - Interactive lock is acquired on `connect()` and released on `disconnect()`.
 *
 * Historical: the previous implementation opened a WebSocket to a browserWebSocket
 * function and tried to forward messages to a non-existent central-server route.
 * That dead path is fully replaced here. See plans/test-like-a-mobile.md.
 */

import { auth, db as firestoreDb } from '@/config/firebase';
import { doc, collection, query, where, getDocs, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { formatOperatorFromMccmnc, looksLikeNumericPlmn } from '@simcheck/shared-types';
import { API_URL, TLAM_PROXY_URL } from '@/config/env';

// All interactive TLAM operations now go through dedicated REST endpoints
// under /api/tlam/sessions/:id/* — voice (dial / hangup / DTMF) and SMS
// (send / own_number). No flow runtime is involved.
// The TLAM browser uses the dedicated streaming HTTP proxy instead (TLAM_PROXY_URL: tlamProxy
// function → central-server /api/tlam-proxy → Pi handle_http_fetch). Not a flow.
const API_BASE_URL = API_URL;

// ─── Event payload types (preserved from the previous implementation so the
//     TLAM components don't need to change) ──────────────────────────────────
export interface PhoneSessionStartMessage {
  type: 'phone_session_start';
  sessionId: string;
  deviceId: string;
  modemImei: string;
  userId: string;
  eSimProfileId?: string;
  organizationId: string;
}

export interface PhoneInitiateCallMessage {
  type: 'phone_initiate_call';
  sessionId: string;
  phoneNumber: string;
  callType: 'voice' | 'volte' | 'vonr';
}

export interface PhoneEndCallMessage {
  type: 'phone_end_call';
  sessionId: string;
}

export interface PhoneSendSmsMessage {
  type: 'phone_send_sms';
  sessionId: string;
  phoneNumber: string;
  message: string;
}

export interface PhoneLoadUrlMessage {
  type: 'phone_load_url';
  sessionId: string;
  url: string;
}

export interface PhoneSessionEndMessage {
  type: 'phone_session_end';
  sessionId: string;
}

export interface PhoneNetworkStatusUpdate {
  type: 'phone_network_status';
  sessionId: string;
  status: 'registering' | 'connected' | 'failed';
  operator?: string;
  technology?: string;
  signalQuality?: number;
  message?: string;
  registered?: boolean;
  /**
   * NSA tier from `tlamNsa.state` written by `captureTlamNsaSnapshot` at TLAM
   * session start. See `classifyNsaState` for tier semantics. Drives the
   * "5G NSA" badge in the TLAM status strip — same tiers ConnectivitySnapshot
   * uses on the Tests page.
   */
  nsaState?: 'nsa_aggregating' | 'nsa_available' | 'endc_capable_no_attach' | 'lte_only' | 'unknown';
  /** The modem's own number (best-effort AT+CNUM, captured at session start).
   *  Often null on prepaid / data / MVNO test SIMs. Shown in the Messages app
   *  so the tester knows what number to text from their own phone. */
  ownNumber?: string | null;
  /** When 1, network has gated this UE off NSA (RestrictDCNR). */
  endcRestrict?: number;
  /** NR secondary cell band number (bare, e.g. 78 for "NR5G BAND N78"). */
  nrBand?: number;
  /** NR Physical Cell ID. */
  nrPci?: number;
  /** NR secondary cell RSRP (dBm). */
  nrRsrp?: number;
}

export interface PhoneCallStatusUpdate {
  type: 'phone_call_status';
  sessionId: string;
  status: 'dialing' | 'ringing' | 'connected' | 'ended' | 'failed';
  duration?: number;
  error?: string;
}

export interface PhoneSmsStatusUpdate {
  type: 'phone_sms_status';
  sessionId: string;
  status: 'sending' | 'sent' | 'delivered' | 'failed';
  messageId?: string;
  error?: string;
}

export interface PhoneIncomingSmsUpdate {
  type: 'phone_incoming_sms';
  sessionId: string;
  from: string;
  message: string;
  timestamp: string;
}

export interface PhonePageStatusUpdate {
  type: 'phone_page_status';
  sessionId: string;
  status: 'connecting' | 'downloading' | 'loaded' | 'failed';
  progress?: number;
  content?: string;
  loadTime?: number;
  dataUsed?: number;
  error?: string;
}

export type PhoneMessage =
  | PhoneSessionStartMessage
  | PhoneInitiateCallMessage
  | PhoneEndCallMessage
  | PhoneSendSmsMessage
  | PhoneLoadUrlMessage
  | PhoneSessionEndMessage;

export type PhoneStatusUpdate =
  | PhoneNetworkStatusUpdate
  | PhoneCallStatusUpdate
  | PhoneSmsStatusUpdate
  | PhoneIncomingSmsUpdate
  | PhonePageStatusUpdate;

type EventCallback = (data: any) => void;

// ─── Service implementation ──────────────────────────────────────────────────
class PhoneService {
  private deviceId: string | null = null;
  private sessionId: string | null = null;
  private modemImei: string | null = null;
  private organizationId: string | null = null;
  private lockId: string | null = null;
  private allocationId: string | null = null;
  private profileId: string | null = null;
  private apn: string | null = null;
  private callStartTime: number | null = null;
  private activeExecUnsub: Unsubscribe | null = null;
  // Current TLAM call (id from /dial response). Cleared on hangup/end/disconnect.
  private currentCallId: string | null = null;
  // Last status we emitted, so we don't fire dupe events when the
  // Firestore doc updates with an unchanged status field.
  private lastEmittedCallStatus: string | null = null;
  // Firestore subscription on `tlam_calls/{callId}` — the SOLE source of
  // call state in iteration-2. Pi's URC monitor pushes +CIEV / +COLP /
  // NO CARRIER through central-server which writes the doc. onSnapshot
  // fires within ~50-200 ms of the URC.
  private callDocUnsub: Unsubscribe | null = null;
  // Live snapshot of devices/{deviceId}/known_modems/{imei} — feeds the
  // TLAM status strip operator / technology / signal %. Refreshed on every
  // Pi heartbeat (~30 s), unsubscribed on disconnect.
  private modemStatusUnsub: Unsubscribe | null = null;
  // One onSnapshot per in-flight outgoing SMS (`tlam_sms/{smsId}`), keyed by
  // smsId — multiple sends can be live at once. Each flips the bubble through
  // sending → sent → delivered (or failed) and self-unsubscribes on a terminal
  // status. Torn down en masse on endSession/disconnect.
  private smsDocUnsubs: Map<string, Unsubscribe> = new Map();
  // Last status emitted per smsId, so an unchanged doc update doesn't re-fire.
  private lastEmittedSmsStatus: Map<string, string> = new Map();
  // Session-level listener for incoming (MT) SMS — one query for the whole
  // session (`tlam_sms where sessionId==X and direction=='incoming'`).
  private incomingSmsUnsub: Unsubscribe | null = null;
  // WebRTC audio bridge — created on /dial success, torn down on hangup.
  // ontrack pipes the remote audio into the <audio> element registered by
  // the dialer UI via setAudioElement().
  // Cached current audio bridge status. Listeners that attach AFTER the
  // bridge starts (e.g. PhoneDialer mounting after the modal opens) miss
  // the synchronous 'connecting' emit fired from setupAudioBridge's first
  // line, leaving the UI stuck at 'idle' (initial React state). Components
  // call getCurrentAudioStatus() on mount to bootstrap their local state
  // from the source of truth.
  private currentAudioStatus: 'idle' | 'connecting' | 'flowing' | 'unavailable' | 'failed' = 'idle';

  private audioPeer: RTCPeerConnection | null = null;
  // Lazy-voice guard: flips true on the first ensureAudioBridge() (Phone open);
  // reset on disconnect so the next session re-arms. Prevents re-probing on
  // repeated dialer opens.
  private _audioBridgeStarted = false;
  private localMicStream: MediaStream | null = null;
  private remoteAudioStream: MediaStream | null = null;
  private audioElement: HTMLAudioElement | null = null;
  private audioTransport: 'uac' | 'qpcmv' | 'none' | 'unknown' = 'unknown';
  private eventListeners: Map<string, Set<EventCallback>> = new Map();
  // Cached token for beforeunload release — we can't `await getIdToken()` from
  // a pagehide handler, so we snapshot the current token at connect time.
  /**
   * Register an already-established TLAM session. The modal owns the session
   * lifecycle (start / heartbeat / end via /api/tlam/sessions) — this method
   * just stores the identifiers so the interactive REST calls (dial / sendSms
   * / loadUrl) can send the correct allocation + interactiveLockId downstream.
   */
  connect(
    deviceId: string,
    sessionId: string,
    modemImei: string,
    organizationId: string,
    lockId: string,
    allocationId?: string,
    profileId?: string,
    apn?: string,
  ): void {
    this.deviceId = deviceId;
    this.sessionId = sessionId;
    this.modemImei = modemImei;
    this.organizationId = organizationId;
    this.lockId = lockId;
    this.allocationId = allocationId || null;
    this.profileId = profileId || null;
    this.apn = apn || null;
    console.log(
      `[lock:web] phoneService.connect sessionId=${sessionId} lockId=${lockId} ` +
      `imei=${modemImei} deviceId=${deviceId} allocationId=${allocationId || '<none>'} ` +
      `(lock already acquired by /tlam/sessions response)`
    );
    this.emit('connected', { deviceId, sessionId });

    // LAZY VOICE: do NOT set up the audio bridge here. It used to run eagerly on
    // every session connect — but on a voice-incapable / slow-UAC modem (e.g.
    // EG25) the probe takes ~25s and 504s, prompts for the mic, and contends
    // with the browser/data bring-up, for a session where the user only wanted
    // to browse. The bridge is now set up on demand the first time the Phone
    // app opens, via ensureAudioBridge(). See PhoneDialer mount.
    if (false && allocationId) {
      console.log(
        `[bind:web] phoneService.connect kicking off setupAudioBridge sessionId=${sessionId}`
      );
      void this.setupAudioBridge(sessionId).catch((err) => {
        console.warn('[bind:web] session-start audio bridge setup failed:', err);
        this.emitAudioStatus({
          sessionId,
          status: 'unavailable',
          error: err?.message || String(err),
        });
      });
    }
  }

  /** The allocation id the modal receives from startTlamSession. Needed for
   *  POST /api/tlam/sessions/:id/prepare so the backend can verify the
   *  caller still owns the allocation before forwarding to the edge. */
  getAllocationId(): string | null {
    return this.allocationId;
  }

  /** The eSIM profile id backing this session. Used to scope the per-session
   *  APN cache so switching profiles/locations never warms a stale APN (which
   *  would force a second full bearer bring-up). */
  getProfileId(): string | null {
    return this.profileId;
  }

  /** The APN chosen for this session (Quick Tests Step 3). PhoneBrowser reads
   *  this so it never has to prompt — the bearer is already being warmed with
   *  this APN at allocation time. */
  getApn(): string | null {
    return this.apn;
  }

  /**
   * Kick off the phone "home" state. In Phase 1 we read the modem's last-known
   * status from Firestore rather than re-registering on session start — doing
   * registration here would send AT+CFUN and friends and slow the UI noticeably.
   * The UI shows whatever the modem is already doing.
   */
  async startSession(
    deviceId: string,
    modemImei: string,
    _userId: string,
    _organizationId: string,
    _eSimProfileId?: string
  ): Promise<void> {
    if (!this.sessionId) {
      throw new Error('Session ID not set. Call connect() first.');
    }

    // Tear down any prior subscription before starting a new one — switching
    // modems mid-session is rare but the cleanup is cheap.
    if (this.modemStatusUnsub) {
      this.modemStatusUnsub();
      this.modemStatusUnsub = null;
    }

    // Source of truth: devices/{deviceId}/known_modems/{imei}. Central-server
    // writes last_known.network_status + last_known.signal_quality on every
    // Pi heartbeat, so subscribing gives us real operator / technology /
    // signal without hardcoded placeholders (the old path read a stale
    // edge_devices/{id}/modems/{imei} doc that didn't exist post-
    // consolidation, which is why the UI showed literal "Network" / "4G LTE"
    // / 81%).
    const ref = doc(firestoreDb, 'devices', deviceId, 'known_modems', modemImei);
    this.modemStatusUnsub = onSnapshot(
      ref,
      (snap) => {
        if (!this.sessionId) return;
        const data = snap.data() as any;
        const lk = data?.last_known;
        const ns = lk?.network_status;
        const sq = lk?.signal_quality;

        // Operator name. The edge reports it under several aliases depending
        // on gateway version — try them all before giving up.
        let operator: string | undefined =
          ns?.network ||
          ns?.connected_operator ||
          ns?.operator ||
          undefined;

        // Safety net for the AT+COPS=1,2,"<MCCMNC>" format-latch (forced PLMN
        // lock). Edge v3.9.2+ restores AT+COPS=3,0 after the lock so this
        // branch shouldn't fire there, but older Pis and any vendor firmware
        // that ignores the format restore will keep returning the raw PLMN —
        // resolve it to a friendly label here so the status strip stays
        // readable.
        if (operator && looksLikeNumericPlmn(operator)) {
          operator = formatOperatorFromMccmnc(operator);
        }

        // Radio technology ("LTE", "WCDMA", "5G SA", etc.)
        const technology: string | undefined =
          ns?.type ||
          ns?.network_mode ||
          ns?.access_technology ||
          undefined;

        // Prefer an explicit 0..100 percentage from the edge. Fall back to
        // deriving one from CSQ (0..31) if that's all we have. Leave
        // undefined when we genuinely don't know — the UI renders "—"
        // rather than guessing.
        let signalPct: number | undefined;
        if (typeof sq?.quality_percentage === 'number') {
          signalPct = Math.max(0, Math.min(100, sq.quality_percentage));
        } else if (typeof sq?.rssi === 'number' && sq.rssi >= 0 && sq.rssi <= 31) {
          signalPct = Math.round((sq.rssi / 31) * 100);
        }

        const registered = !!operator;

        // NSA snapshot — captureTlamNsaSnapshot writes this at session start.
        // Survives heartbeats because central-server uses { merge: true } on
        // known_modems writes. Absent on non-5G modems / when probe failed.
        const tlamNsa = data?.tlamNsa as Record<string, any> | undefined;

        this.emit('phone_network_status', {
          type: 'phone_network_status',
          sessionId: this.sessionId,
          status: registered ? 'connected' : 'registering',
          operator,
          technology,
          signalQuality: signalPct,
          registered,
          // captureTlamSmsInit writes known_modems/{imei}.ownNumber at session
          // start (best-effort AT+CNUM). null when the SIM doesn't expose it.
          ownNumber: (data?.ownNumber ?? null) as string | null,
          nsaState: tlamNsa?.state,
          endcRestrict: typeof tlamNsa?.endcRestrict === 'number' ? tlamNsa.endcRestrict : undefined,
          nrBand: typeof tlamNsa?.nrBand === 'number' ? tlamNsa.nrBand : undefined,
          nrPci: typeof tlamNsa?.nrPci === 'number' ? tlamNsa.nrPci : undefined,
          nrRsrp: typeof tlamNsa?.nrRsrp === 'number' ? tlamNsa.nrRsrp : undefined,
        } as PhoneNetworkStatusUpdate);
      },
      (err) => {
        console.error('[phoneService] modem snapshot error:', err);
      }
    );

    // Start listening for incoming (MT) SMS for this session.
    this.subscribeToIncomingSms();
  }

  /**
   * Dial a phone number via `POST /api/tlam/sessions/:id/dial`. The backend
   * fires ATD on the locked modem and returns a `callId`. We then poll
   * `GET /tlam/sessions/:id/call_state` every ~2 s — the polling lives in
   * the browser tab so it survives the api Cloud Function freezing its
   * instance after the /dial response returns.
   */
  async initiateCall(phoneNumber: string, _callType: 'voice' | 'volte' | 'vonr' = 'voice'): Promise<void> {
    if (!this.sessionId) throw new Error('Session ID not set. Call connect() first.');
    if (!this.lockId) throw new Error('No active interactive lock. Call connect() first.');
    if (!this.allocationId) throw new Error('No allocation. Call connect() first.');

    this.callStartTime = Date.now();
    this.lastEmittedCallStatus = null;

    // Optimistic 'dialing' so the UI flips immediately. The Firestore
    // subscription below will overwrite this once the first poll lands.
    this.emit('phone_call_status', {
      type: 'phone_call_status',
      sessionId: this.sessionId,
      status: 'dialing',
    } as PhoneCallStatusUpdate);

    const user = auth.currentUser;
    if (!user) {
      this.emitCallFailure('Not authenticated');
      return;
    }
    const token = await user.getIdToken();

    // Generate the callId client-side so we can fire /dial AND the audio
    // bridge in parallel. Without this they serialize: /dial waits for the
    // Pi's ATD OK (5–8 s on EG25 firmware where OK = call answered), then
    // the bridge starts (mint creds + getUserMedia + ICE + offer/answer +
    // ICE complete = another 5–8 s). Net effect on a quick peer-decline
    // call: bridge ICE finishes AFTER NO CARRIER and audio never flows.
    const uuid = (crypto as any).randomUUID
      ? (crypto as any).randomUUID()
      : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}-${Math.random().toString(16).slice(2, 10)}-${Math.random().toString(16).slice(2, 10)}-${Math.random().toString(16).slice(2, 14)}`;
    const callId = `tlam_call_${uuid}`;
    this.currentCallId = callId;

    // Note: we subscribe to the Firestore doc only AFTER /dial returns.
    // The doc doesn't exist before /dial creates it, and the rule on
    // `tlam_calls/{callId}` requires `resource.data.userId == auth.uid` —
    // a snapshot before the doc is written errors with permission-denied
    // and the listener stays broken even after the doc materialises.

    try {
      const dialPromise = fetch(
        `${API_BASE_URL}/tlam/sessions/${encodeURIComponent(this.sessionId)}/dial`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ allocationId: this.allocationId, phoneNumber, callId }),
        },
      );

      // Audio bridge is already up — set up at session start in connect()
      // and persists across all dials in this session. Per-call work is
      // just /dial. The bridge picks up the call's voice the moment ATD's
      // voice path connects on the modem (UAC stream is always live, the
      // modem just routes voice into it when a call is active).

      const response = await dialPromise;
      const data = await response.json().catch(() => ({} as any));
      if (!response.ok) {
        this.emitCallFailure(data.message || data.reason || `HTTP ${response.status}`);
        return;
      }

      // Doc exists now (server creates it before dispatching) — safe to
      // open the Firestore listener. Use whichever callId the server
      // returned in case it rejected our client-generated one (rare; only
      // on schema mismatch).
      const finalCallId = data.callId || callId;
      this.currentCallId = finalCallId;
      this.subscribeToCallDoc(finalCallId);
    } catch (error: any) {
      console.error('[phoneService] initiateCall failed:', error);
      this.emitCallFailure(error?.message || String(error));
    }
  }

  /**
   * Hang up the current call. Updates UI to 'ended' IMMEDIATELY (optimistic)
   * and runs the backend `POST /tlam/sessions/:id/hangup` + audio bridge
   * teardown in parallel so the UI doesn't sit waiting on the AT+CHUP
   * round-trip (5–15 s if the modem's slow to reply).
   */
  endCall(): void {
    if (!this.sessionId || !this.allocationId) return;

    const callIdAtHangup = this.currentCallId;
    const sessionIdAtHangup = this.sessionId;
    const allocationIdAtHangup = this.allocationId;
    const duration = this.callStartTime ? Math.floor((Date.now() - this.callStartTime) / 1000) : 0;

    // 1. Tear down the URC subscription first so a stale push can't
    //    re-emit 'connected' on top of our 'ended'.
    this.unsubscribeFromCallDoc();

    // 2. Optimistic UI flip — happens synchronously before any awaits.
    this.lastEmittedCallStatus = 'ended';
    this.emit('phone_call_status', {
      type: 'phone_call_status',
      sessionId: sessionIdAtHangup,
      status: 'ended',
      duration,
    } as PhoneCallStatusUpdate);

    // 3. Tear down local state so a subsequent call gets a fresh peer.
    this.callStartTime = null;
    this.currentCallId = null;
    void this.teardownAudioBridge().catch(() => { /* ignore */ });

    // 4. Fire the backend hangup in the background. We don't surface
    //    failures to the user — the modem's call will time out on its own
    //    eventually if AT+CHUP doesn't land, and the allocation's 15 min
    //    TTL is the ultimate safety net.
    void (async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;
        const token = await user.getIdToken();
        await fetch(
          `${API_BASE_URL}/tlam/sessions/${encodeURIComponent(sessionIdAtHangup)}/hangup`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ allocationId: allocationIdAtHangup, callId: callIdAtHangup }),
          },
        );
      } catch (err) {
        console.warn('[phoneService] hangup background failed:', err);
      }
    })();
  }

  /**
   * Send DTMF digits while connected. Posts to `/api/tlam/sessions/:id/dtmf`.
   * Edge runs `AT+VTS=<d>` per digit. Caller must be in 'connected' state.
   */
  async sendDtmf(digits: string): Promise<void> {
    if (!this.sessionId) throw new Error('Session ID not set. Call connect() first.');
    if (!this.allocationId) throw new Error('No allocation. Call connect() first.');
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    const token = await user.getIdToken();

    const response = await fetch(`${API_BASE_URL}/tlam/sessions/${encodeURIComponent(this.sessionId)}/dtmf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ allocationId: this.allocationId, callId: this.currentCallId, digits }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({} as any));
      throw new Error(data.message || data.reason || `HTTP ${response.status}`);
    }
  }

  /**
   * Open a bidirectional raw-PCM audio stream for the active call and return a
   * consumer WebSocket URL + short-lived token. Hand these to an external
   * (BYO) AI agent so it can listen, talk, and drive the call. The browser's
   * own WebRTC audio bridge is unaffected (the Pi fans capture out to both).
   */
  async startAudioStream(
    encoding: 's16le' | 'mulaw' = 's16le',
  ): Promise<{ wsUrl: string; token: string; encoding: string; expiresAt: string }> {
    if (!this.sessionId) throw new Error('Session ID not set. Call connect() first.');
    if (!this.allocationId) throw new Error('No allocation. Call connect() first.');
    if (!this.currentCallId) throw new Error('No active call to stream.');
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    const token = await user.getIdToken();

    const response = await fetch(
      `${API_BASE_URL}/tlam/sessions/${encodeURIComponent(this.sessionId)}/stream/start`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ allocationId: this.allocationId, callId: this.currentCallId, encoding }),
      },
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({} as any));
      throw new Error(data.message || data.error || `HTTP ${response.status}`);
    }
    return response.json();
  }

  // ── Internal call-doc subscription helpers ──────────────────────────────
  private emitCallFailure(error: string): void {
    if (!this.sessionId) return;
    this.emit('phone_call_status', {
      type: 'phone_call_status',
      sessionId: this.sessionId,
      status: 'failed',
      error,
    } as PhoneCallStatusUpdate);
  }

  // Iteration-2: Firestore subscription is the SOLE source of call state.
  // Pi's URC monitor pushes +CIEV / +COLP / NO CARRIER through central-
  // server → tlam_calls/{callId}. Browser onSnapshot fires within ~50-200
  // ms of the URC. No polling — no AT contention with the call.
  private subscribeToCallDoc(callId: string): void {
    this.unsubscribeFromCallDoc();
    console.log('[phoneService] subscribeToCallDoc:', callId);
    const ref = doc(firestoreDb, 'tlam_calls', callId);
    this.callDocUnsub = onSnapshot(
      ref,
      (snap) => {
        const exists = snap.exists();
        const data = exists ? (snap.data() as any) : null;
        console.log('[phoneService] tlam_calls snapshot:', {
          callId,
          exists,
          status: data?.status,
          lastUpdateSource: data?.lastUpdateSource,
          endReason: data?.endReason,
          sessionIdSet: !!this.sessionId,
        });
        if (!exists || !this.sessionId) return;
        const status = data?.status as string | undefined;
        if (!status) return;
        this.handleCallStateUpdate(status, !!data.endError && data.endError);
      },
      (err) => {
        console.warn('[phoneService] tlam_calls snapshot error:', err);
      },
    );
  }

  private unsubscribeFromCallDoc(): void {
    if (this.callDocUnsub) {
      try { this.callDocUnsub(); } catch { /* ignore */ }
      this.callDocUnsub = null;
    }
  }

  /**
   * Map a Firestore doc status → UI emit. The doc is URC-driven so we
   * trust it (the modem emits NO CARRIER / +CIEV exactly once per
   * transition). Iteration-2 dropped the dual-confirm + polling debounce
   * that iteration-1 needed.
   */
  private handleCallStateUpdate(
    docStatus: string,
    endError?: string | false,
  ): void {
    if (!this.sessionId) return;

    // Edge-side statuses: 'dialing' | 'alerting' | 'connected' | 'ended' |
    // 'failed' | 'held' | 'incoming' | 'waiting'.
    // UI-side: 'dialing' | 'ringing' | 'connected' | 'ended' | 'failed'.
    const uiStatus =
      docStatus === 'connected' ? 'connected'
      : docStatus === 'alerting' ? 'ringing'
      : docStatus === 'dialing' ? 'dialing'
      : docStatus === 'ended' ? 'ended'
      : docStatus === 'failed' ? 'failed'
      : null;

    if (!uiStatus) return; // held / incoming / waiting — ignore for now
    if (uiStatus === this.lastEmittedCallStatus) {
      console.log('[phoneService] handleCallStateUpdate dedup-skip:', { uiStatus, lastEmittedCallStatus: this.lastEmittedCallStatus });
      return;
    }
    console.log('[phoneService] handleCallStateUpdate emit:', { docStatus, uiStatus, prev: this.lastEmittedCallStatus });
    this.lastEmittedCallStatus = uiStatus;

    const duration = this.callStartTime
      ? Math.floor((Date.now() - this.callStartTime) / 1000)
      : 0;

    this.emit('phone_call_status', {
      type: 'phone_call_status',
      sessionId: this.sessionId,
      status: uiStatus as PhoneCallStatusUpdate['status'],
      duration,
      ...(endError ? { error: String(endError) } : {}),
    } as PhoneCallStatusUpdate);

    if (uiStatus === 'ended' || uiStatus === 'failed') {
      // Modem reported call gone (remote hangup / network drop / failure).
      // Tear down audio bridge + tell backend to release the allocation.
      // AT+CHUP on an already-ended call is a no-op so safe.
      const sessionIdAtEnd = this.sessionId;
      const allocationIdAtEnd = this.allocationId;
      const callIdAtEnd = this.currentCallId;
      this.callStartTime = null;
      this.currentCallId = null;
      void this.teardownAudioBridge().catch(() => { /* ignore */ });
      if (sessionIdAtEnd && allocationIdAtEnd && callIdAtEnd) {
        void (async () => {
          try {
            const user = auth.currentUser;
            if (!user) return;
            const token = await user.getIdToken();
            await fetch(
              `${API_BASE_URL}/tlam/sessions/${encodeURIComponent(sessionIdAtEnd)}/hangup`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ allocationId: allocationIdAtEnd, callId: callIdAtEnd }),
              },
            );
          } catch { /* best effort */ }
        })();
      }
    }
  }

  // ── WebRTC audio bridge ─────────────────────────────────────────────────

  /**
   * Register the <audio> element that should play remote audio. Called by
   * PhoneDialer.tsx on mount; cleared on unmount. The dialer keeps a hidden
   * `<audio autoPlay />` mounted so we always have a sink to attach to.
   */
  setAudioElement(el: HTMLAudioElement | null): void {
    this.audioElement = el;
    if (el && this.remoteAudioStream) {
      el.srcObject = this.remoteAudioStream;
      el.play().catch(() => { /* autoplay may be blocked until user gesture */ });
    }
  }

  /**
   * Toggle the local mic. Disabled tracks still flow but as silence.
   */
  setMuted(muted: boolean): void {
    if (!this.localMicStream) return;
    for (const track of this.localMicStream.getAudioTracks()) {
      track.enabled = !muted;
    }
  }

  isMuted(): boolean {
    if (!this.localMicStream) return false;
    return this.localMicStream.getAudioTracks().some((t) => !t.enabled);
  }

  getAudioTransport(): 'uac' | 'qpcmv' | 'none' | 'unknown' {
    return this.audioTransport;
  }

  /** Current audio bridge status. UI components call this on mount so a
   *  late-attached `phone_audio_status` listener doesn't miss the
   *  synchronous 'connecting' emit fired before the listener registered. */
  getCurrentAudioStatus(): 'idle' | 'connecting' | 'flowing' | 'unavailable' | 'failed' {
    return this.currentAudioStatus;
  }

  /** Wrapper around `emit('phone_audio_status', ...)` that caches the
   *  status so newly-mounted listeners can sync via getCurrentAudioStatus(). */
  private emitAudioStatus(payload: {
    sessionId: string | null;
    status: 'connecting' | 'flowing' | 'unavailable' | 'failed';
    error?: string;
  }): void {
    this.currentAudioStatus = payload.status;
    this.emit('phone_audio_status', {
      type: 'phone_audio_status',
      sessionId: payload.sessionId,
      status: payload.status,
      ...(payload.error ? { error: payload.error } : {}),
    });
  }

  /**
   * Lazily set up the voice audio bridge — called the first time the Phone app
   * opens (NOT on session connect). Idempotent: repeated calls while a setup is
   * in flight or already done are no-ops, so re-opening the dialer doesn't
   * re-probe. Browser-only / Messages-only sessions never trigger the 25s EG25
   * voice probe or the mic prompt.
   */
  ensureAudioBridge(): void {
    if (!this.sessionId || !this.allocationId) return;
    if (this._audioBridgeStarted) return;
    this._audioBridgeStarted = true;
    const sid = this.sessionId;
    console.log(`[bind:web] ensureAudioBridge (lazy, on Phone open) sessionId=${sid}`);
    void this.setupAudioBridge(sid).catch((err) => {
      console.warn('[bind:web] lazy audio bridge setup failed:', err);
      this.emitAudioStatus({ sessionId: sid, status: 'unavailable' });
    });
  }

  private async setupAudioBridge(callId: string): Promise<void> {
    if (!this.sessionId || !this.allocationId) {
      throw new Error('Session not initialised');
    }
    const t0 = Date.now();
    console.log(
      `[bind:web] setupAudioBridge START sessionId=${this.sessionId} callId=${callId} ` +
      `allocationId=${this.allocationId}`
    );

    // Surface "audio is being set up" to the UI so the user sees progress
    // during the parallel /dial + bridge phase, instead of silence until
    // either 'flowing' (success) or 'failed' (~10 s later). Goes through
    // emitAudioStatus so a late-attached PhoneDialer listener can pick up
    // the current state via getCurrentAudioStatus() on mount.
    this.emitAudioStatus({ sessionId: this.sessionId, status: 'connecting' });

    // 1. Mint TURN credentials (if coturn is configured server-side; falls
    //    back to STUN-only otherwise).
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    const token = await user.getIdToken();

    const tCreds = Date.now();
    console.log(`[bind:web] GET /audio/credentials START callId=${callId}`);
    const credsResp = await fetch(
      `${API_BASE_URL}/tlam/sessions/${encodeURIComponent(this.sessionId)}/audio/credentials` +
        `?callId=${encodeURIComponent(callId)}&allocationId=${encodeURIComponent(this.allocationId)}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    console.log(
      `[bind:web] GET /audio/credentials DONE callId=${callId} status=${credsResp.status} ` +
      `ms=${Date.now() - tCreds}`
    );
    if (!credsResp.ok) {
      throw new Error(`audio/credentials HTTP ${credsResp.status}`);
    }
    const creds = await credsResp.json();
    const iceServers: RTCIceServer[] = (creds.iceServers || []).map((s: any) => ({
      urls: s.urls,
      ...(s.username ? { username: s.username } : {}),
      ...(s.credential ? { credential: s.credential } : {}),
    }));
    console.log(
      `[bind:web] iceServers parsed callId=${callId} count=${iceServers.length}`
    );

    // 2. getUserMedia for the browser mic.
    const tMic = Date.now();
    let mic: MediaStream;
    try {
      mic = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    } catch (err: any) {
      console.warn(`[bind:web] getUserMedia FAILED callId=${callId} err=${err?.message || err}`);
      throw new Error(`Microphone access denied: ${err?.message || err}`);
    }
    this.localMicStream = mic;
    console.log(
      `[bind:web] getUserMedia OK callId=${callId} tracks=${mic.getAudioTracks().length} ` +
      `ms=${Date.now() - tMic}`
    );

    // 3. Build the peer connection.
    const pc = new RTCPeerConnection({ iceServers });
    this.audioPeer = pc;
    for (const track of mic.getAudioTracks()) {
      pc.addTrack(track, mic);
    }
    console.log(`[bind:web] RTCPeerConnection built callId=${callId} ice_count=${iceServers.length}`);

    pc.ontrack = (e) => {
      // Remote audio (modem mic) — pipe into the registered <audio> element.
      const stream = e.streams && e.streams[0]
        ? e.streams[0]
        : new MediaStream([e.track]);
      this.remoteAudioStream = stream;
      console.log(
        `[bind:web] pc.ontrack fired callId=${callId} kind=${e.track.kind} ` +
        `audio_element_attached=${!!this.audioElement}`
      );
      if (this.audioElement) {
        this.audioElement.srcObject = stream;
        this.audioElement.play()
          .then(() => console.log(`[bind:web] <audio>.play() OK callId=${callId}`))
          .catch((err) => console.warn(
            `[bind:web] <audio>.play() FAILED callId=${callId} (autoplay block?) err=${err?.message || err}`
          ));
      }
      this.emitAudioStatus({ sessionId: this.sessionId, status: 'flowing' });
    };

    pc.oniceconnectionstatechange = () => {
      console.log(`[bind:web] ICE state callId=${callId} → ${pc.iceConnectionState}`);
      if (pc.iceConnectionState === 'failed') {
        this.emitAudioStatus({
          sessionId: this.sessionId,
          status: 'failed',
          error: 'ICE connection failed (likely NAT traversal — TURN may be unreachable)',
        });
        // ICE-failed is also a strong signal the call is gone (peer closed
        // the bridge, modem rebooted, network dropped). Force-end the call
        // so we don't leave the UI sitting on 'connected' when the audio
        // path has been dead for ~30s.
        this.handleCallStateUpdate('ended');
      }
    };

    // 4. Generate offer, wait for ICE gathering complete (vanilla ICE).
    // 1.5 s is plenty for STUN+TURN candidates to gather; 5 s was paying
    // double the price (browser side + Pi side) and pushing total bridge-
    // setup time past 10 s. STUN candidate from Google's public server
    // returns in <500 ms; TURN allocation in <1 s.
    const tOffer = Date.now();
    const offer = await pc.createOffer({ offerToReceiveAudio: true });
    await pc.setLocalDescription(offer);
    console.log(
      `[bind:web] createOffer+setLocalDescription_done callId=${callId} ms=${Date.now() - tOffer}`
    );
    const tIce = Date.now();
    await this.waitForIceComplete(pc, 1500);
    console.log(
      `[bind:web] waitForIceComplete done callId=${callId} state=${pc.iceGatheringState} ` +
      `ms=${Date.now() - tIce}`
    );

    // 5. POST to /audio/offer — backend forwards to Pi via central-server.
    const tHttp = Date.now();
    console.log(
      `[bind:web] POST /audio/offer START callId=${callId} sdp_len=${(pc.localDescription?.sdp || '').length}`
    );
    const offerResp = await fetch(
      `${API_BASE_URL}/tlam/sessions/${encodeURIComponent(this.sessionId)}/audio/offer`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          allocationId: this.allocationId,
          callId,
          sdp: pc.localDescription?.sdp,
          iceServers: creds.iceServers,
        }),
      },
    );
    console.log(
      `[bind:web] POST /audio/offer DONE callId=${callId} status=${offerResp.status} ` +
      `ms=${Date.now() - tHttp}`
    );
    if (!offerResp.ok) {
      const errBody = await offerResp.json().catch(() => ({} as any));
      console.warn(
        `[bind:web] POST /audio/offer FAILED callId=${callId} status=${offerResp.status} ` +
        `reason=${errBody.reason || errBody.error || ''}`
      );
      throw new Error(`audio/offer HTTP ${offerResp.status}: ${errBody.reason || errBody.error || ''}`);
    }
    const answer = await offerResp.json();
    this.audioTransport = answer.transport || 'unknown';
    console.log(
      `[bind:web] /audio/offer body callId=${callId} transport=${answer.transport} ` +
      `alsa_card=${answer.alsaCard ?? '-'} pi_total_ms=${answer.totalDurationMs}`
    );
    if (answer.transport && answer.transport !== 'uac') {
      console.warn(
        `[bind:web] setupAudioBridge ABORT_NO_UAC callId=${callId} transport=${answer.transport}`
      );
      this.emitAudioStatus({
        sessionId: this.sessionId,
        status: 'unavailable',
        error: `No audio on this modem (transport=${answer.transport})`,
      });
      return;
    }

    await pc.setRemoteDescription({ type: 'answer', sdp: answer.sdp });
    console.log(
      `[bind:web] setupAudioBridge DONE callId=${callId} setRemoteDescription_ok ` +
      `total_ms=${Date.now() - t0}`
    );
  }

  private waitForIceComplete(pc: RTCPeerConnection, timeoutMs: number): Promise<void> {
    if (pc.iceGatheringState === 'complete') return Promise.resolve();
    return new Promise((resolve) => {
      const onChange = () => {
        if (pc.iceGatheringState === 'complete') {
          pc.removeEventListener('icegatheringstatechange', onChange);
          clearTimeout(timer);
          resolve();
        }
      };
      const timer = setTimeout(() => {
        pc.removeEventListener('icegatheringstatechange', onChange);
        resolve();
      }, timeoutMs);
      pc.addEventListener('icegatheringstatechange', onChange);
    });
  }

  private async teardownAudioBridge(): Promise<void> {
    const pc = this.audioPeer;
    const mic = this.localMicStream;
    const sessionId = this.sessionId;
    const callId = this.currentCallId;
    console.log(
      `[bind:web] teardownAudioBridge START sessionId=${sessionId} callId=${callId || '-'} ` +
      `had_pc=${!!pc} had_mic=${!!mic}`
    );
    this.audioPeer = null;
    this.localMicStream = null;
    this.remoteAudioStream = null;
    this.audioTransport = 'unknown';
    this.currentAudioStatus = 'idle';

    if (this.audioElement) {
      try {
        this.audioElement.pause();
        this.audioElement.srcObject = null;
      } catch { /* ignore */ }
    }
    if (mic) {
      for (const track of mic.getTracks()) {
        try { track.stop(); } catch { /* ignore */ }
      }
    }
    if (pc) {
      try { pc.close(); } catch { /* ignore */ }
    }

    // Best-effort tell the Pi to close its bridge too. Fire-and-forget; the
    // Pi also tears down on its own when the ICE connection drops.
    if (this.sessionId && this.allocationId && this.currentCallId) {
      try {
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken();
          const teardownResp = await fetch(
            `${API_BASE_URL}/tlam/sessions/${encodeURIComponent(this.sessionId)}/audio/teardown`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ allocationId: this.allocationId, callId: this.currentCallId }),
            },
          );
          console.log(
            `[bind:web] teardownAudioBridge POST /audio/teardown DONE callId=${this.currentCallId} ` +
            `status=${teardownResp.status}`
          );
        }
      } catch (err: any) {
        console.warn(`[bind:web] teardownAudioBridge POST /audio/teardown failed: ${err?.message || err}`);
      }
    }
    console.log(`[bind:web] teardownAudioBridge DONE sessionId=${sessionId}`);
  }

  /**
   * Send a real SMS through the locked modem (Phase 2).
   *
   * Uses the awaitPrompt/thenSend extension on atCommand nodes — the
   * compound payload (AT+CMGS="number"\r\nbody\u001A) is sent in one serial
   * write so the modem's input buffer transitions through prompt mode
   * cleanly. No Pi-side changes required.
   *
   * Status flow (all tagged with `messageId` = the smsId so the UI bubble can
   * correlate): 'sending' optimistically → 'sent' (the POST stamps the doc and
   * we subscribe) → 'delivered' (✓✓, when the +CDS delivery report arrives —
   * best-effort, network-dependent) or 'failed'.
   */
  async sendSms(phoneNumber: string, message: string, smsId?: string): Promise<void> {
    if (!this.sessionId) throw new Error('Session ID not set. Call connect() first.');
    if (!this.lockId) throw new Error('No active interactive lock. Call connect() first.');
    if (!this.allocationId) throw new Error('No allocation. Call connect() first.');
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    // Client-generate the id (or accept the caller's, e.g. the bubble id from
    // PhoneMessaging) so the optimistic bubble correlates to the tlam_sms doc.
    const id = (smsId && /^tlam_sms_[0-9a-f-]{36}$/i.test(smsId))
      ? smsId
      : `tlam_sms_${crypto.randomUUID()}`;

    this.emit('phone_sms_status', {
      type: 'phone_sms_status',
      sessionId: this.sessionId,
      status: 'sending',
      messageId: id,
    } as PhoneSmsStatusUpdate);

    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${API_BASE_URL}/tlam/sessions/${encodeURIComponent(this.sessionId)}/sms`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ allocationId: this.allocationId, phoneNumber, message, smsId: id }),
        },
      );
      const data = await response.json().catch(() => ({} as any));
      if (!response.ok) {
        this.emit('phone_sms_status', {
          type: 'phone_sms_status',
          sessionId: this.sessionId,
          status: 'failed',
          messageId: id,
          error: data.message || data.reason || `HTTP ${response.status}`,
        } as PhoneSmsStatusUpdate);
        return;
      }
      // The POST already stamped status='sent'. Subscribe so a later +CDS
      // delivery report flips the bubble to 'delivered'.
      const finalId = (typeof data.smsId === 'string' && data.smsId) || id;
      this.subscribeToSmsDoc(finalId);
    } catch (error: any) {
      console.error('[phoneService] sendSms failed:', error);
      this.emit('phone_sms_status', {
        type: 'phone_sms_status',
        sessionId: this.sessionId,
        status: 'failed',
        messageId: id,
        error: error?.message || String(error),
      } as PhoneSmsStatusUpdate);
    }
  }

  // Subscribe to an outgoing SMS doc (`tlam_sms/{smsId}`). The api function
  // creates it (sending → sent); the Pi's +CDS URC monitor → central-server
  // flips it to 'delivered' (or 'failed'). Self-unsubscribes on terminal.
  private subscribeToSmsDoc(smsId: string): void {
    const existing = this.smsDocUnsubs.get(smsId);
    if (existing) { try { existing(); } catch { /* ignore */ } this.smsDocUnsubs.delete(smsId); }
    const ref = doc(firestoreDb, 'tlam_sms', smsId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists() || !this.sessionId) return;
        const data = snap.data() as any;
        const status = data?.status as string | undefined;
        if (!status) return;
        if (this.lastEmittedSmsStatus.get(smsId) === status) return;
        this.lastEmittedSmsStatus.set(smsId, status);
        this.emit('phone_sms_status', {
          type: 'phone_sms_status',
          sessionId: this.sessionId,
          status: status as PhoneSmsStatusUpdate['status'],
          messageId: smsId,
          error: data?.error || undefined,
        } as PhoneSmsStatusUpdate);
        if (status === 'delivered' || status === 'failed') {
          const u = this.smsDocUnsubs.get(smsId);
          if (u) { try { u(); } catch { /* ignore */ } this.smsDocUnsubs.delete(smsId); }
          this.lastEmittedSmsStatus.delete(smsId);
        }
      },
      (err) => console.warn('[phoneService] tlam_sms snapshot error:', err),
    );
    this.smsDocUnsubs.set(smsId, unsub);
  }

  // Session-level listener for incoming (MT) SMS. Central-server creates
  // `tlam_sms` docs with direction='incoming' when the modem receives a text.
  // Equality-only query (no orderBy) → no composite index needed; we surface
  // only newly-added docs so reconnects don't replay the whole thread.
  /**
   * Load the session's full SMS thread (both directions) from Firestore.
   * The Messages app seeds its thread from this on mount, so messages that
   * arrived while the app was closed still display (issue #166) — the live
   * incoming subscription and per-doc status listeners layer on top.
   */
  async loadSmsHistory(): Promise<Array<{
    id: string;
    direction: 'outgoing' | 'incoming';
    body: string;
    counterparty: string | null;
    status: string | null;
    createdAt: Date;
  }>> {
    if (!this.sessionId) return [];
    const q = query(
      collection(firestoreDb, 'tlam_sms'),
      where('sessionId', '==', this.sessionId),
    );
    const snap = await getDocs(q);
    const rows = snap.docs.map((d) => {
      const data = d.data() as any;
      return {
        id: (data.smsId as string) || d.id,
        direction: (data.direction === 'incoming' ? 'incoming' : 'outgoing') as 'incoming' | 'outgoing',
        body: (data.body as string) || '',
        counterparty: (data.phoneNumber as string) || null,
        status: (data.status as string) || null,
        createdAt: data.createdAt?.toDate?.() || new Date(0),
      };
    });
    rows.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    return rows;
  }

  private subscribeToIncomingSms(): void {
    if (this.incomingSmsUnsub) { try { this.incomingSmsUnsub(); } catch { /* ignore */ } this.incomingSmsUnsub = null; }
    if (!this.sessionId) return;
    const q = query(
      collection(firestoreDb, 'tlam_sms'),
      where('sessionId', '==', this.sessionId),
      where('direction', '==', 'incoming'),
    );
    this.incomingSmsUnsub = onSnapshot(
      q,
      (snap) => {
        if (!this.sessionId) return;
        snap.docChanges().forEach((change) => {
          if (change.type !== 'added') return;
          const data = change.doc.data() as any;
          const createdAt = data?.createdAt?.toDate?.();
          this.emit('phone_incoming_sms', {
            type: 'phone_incoming_sms',
            sessionId: this.sessionId,
            from: data?.phoneNumber || 'Unknown',
            message: data?.body || '',
            timestamp: (createdAt ? createdAt : new Date()).toISOString(),
          } as PhoneIncomingSmsUpdate);
        });
      },
      (err) => console.warn('[phoneService] tlam_sms incoming snapshot error:', err),
    );
  }

  private teardownSmsSubscriptions(): void {
    for (const unsub of this.smsDocUnsubs.values()) {
      try { unsub(); } catch { /* ignore */ }
    }
    this.smsDocUnsubs.clear();
    this.lastEmittedSmsStatus.clear();
    if (this.incomingSmsUnsub) { try { this.incomingSmsUnsub(); } catch { /* ignore */ } this.incomingSmsUnsub = null; }
  }

  /**
   * Load a URL through the TLAM streaming HTTP proxy (Phase 3).
   *
   * Flow:
   *   1. POST /tlamProxy/mint with the interactive lock → get back a short-lived
   *      proxy URL pointing at central-server/api/tlam-proxy/:token
   *   2. Emit phone_page_status: 'connecting' with the proxyUrl in the `content`
   *      field. PhoneBrowser.tsx reads `content` and sets it as the iframe src.
   *   3. The iframe makes a real HTTPS request; central-server streams the Pi's
   *      http_fetch_chunk bytes as the HTTP response body. Browser renders it.
   *
   * There is no Firestore in the data path. The iframe handles all of the
   * loading UI itself (address bar, spinner, etc. live inside PhoneBrowser).
   */
  async loadUrl(url: string, apn?: string): Promise<void> {
    if (!this.sessionId) throw new Error('Session ID not set. Call connect() first.');
    if (!this.lockId) throw new Error('No active interactive lock. Call connect() first.');
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    this.emit('phone_page_status', {
      type: 'phone_page_status',
      sessionId: this.sessionId,
      status: 'connecting',
      progress: 10,
    } as PhonePageStatusUpdate);

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${TLAM_PROXY_URL}/mint`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          interactiveLockId: this.lockId,
          url,
          apn,
          organizationId: this.organizationId,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        this.emit('phone_page_status', {
          type: 'phone_page_status',
          sessionId: this.sessionId,
          status: 'failed',
          error: data.message || data.error || `HTTP ${response.status}`,
        } as PhonePageStatusUpdate);
        return;
      }

      // Hand the iframe the URL. PhoneBrowser.tsx listens for this event,
      // sets iframe.src, and emits the final `loaded` / `failed` status from
      // the iframe's own onLoad / onError — which actually correlates with
      // bytes on screen. We used to fire a synthetic `loaded` 250 ms after
      // this point, but that was a lie: on a slow cellular fetch the spinner
      // would vanish long before any pixels arrived.
      this.emit('phone_page_status', {
        type: 'phone_page_status',
        sessionId: this.sessionId,
        status: 'downloading',
        progress: 50,
        content: data.proxyUrl,
      } as PhonePageStatusUpdate);
    } catch (error: any) {
      console.error('[phoneService] loadUrl failed:', error);
      this.emit('phone_page_status', {
        type: 'phone_page_status',
        sessionId: this.sessionId,
        status: 'failed',
        error: error?.message || String(error),
      } as PhonePageStatusUpdate);
    }
  }

  /**
   * End the phone session. No server-side work in Phase 1 — the modem returns
   * to idle as soon as the interactive lock is released on disconnect().
   */
  endSession(): void {
    if (this.activeExecUnsub) {
      this.activeExecUnsub();
      this.activeExecUnsub = null;
    }
    if (this.modemStatusUnsub) {
      this.modemStatusUnsub();
      this.modemStatusUnsub = null;
    }
    this.unsubscribeFromCallDoc();
    this.teardownSmsSubscriptions();
    void this.teardownAudioBridge().catch(() => { /* ignore */ });
    this.currentCallId = null;
    this.lastEmittedCallStatus = null;
  }

  /**
   * Release the interactive lock and clear state. Safe to call more than once.
   */
  /**
   * Clear local state. Allocation/lock release is handled by the modal via
   * `endTlamSession()` against /api/tlam/sessions — this method does not
   * talk to the backend.
   */
  disconnect(): void {
    console.log(
      `[release:web] phoneService.disconnect entry sessionId=${this.sessionId} ` +
      `lockId=${this.lockId} allocationId=${this.allocationId} ` +
      `(modal-side endTlamSession will issue DELETE /tlam/sessions which actually releases the lock)`
    );
    if (this.activeExecUnsub) {
      this.activeExecUnsub();
      this.activeExecUnsub = null;
    }
    if (this.modemStatusUnsub) {
      this.modemStatusUnsub();
      this.modemStatusUnsub = null;
    }
    this.unsubscribeFromCallDoc();
    void this.teardownAudioBridge().catch(() => { /* ignore */ });
    const releasedLockId = this.lockId;
    this.deviceId = null;
    this.sessionId = null;
    this.modemImei = null;
    this.organizationId = null;
    this.lockId = null;
    this.allocationId = null;
    this._audioBridgeStarted = false;
    this.callStartTime = null;
    this.currentCallId = null;
    this.lastEmittedCallStatus = null;
    if (releasedLockId) this.emit('lock_released', { lockId: releasedLockId });
  }

  // ─── Event emitter API (same shape as the previous WebSocket service) ─────
  on(event: string, callback: EventCallback): void {
    if (!this.eventListeners.has(event)) this.eventListeners.set(event, new Set());
    this.eventListeners.get(event)!.add(callback);
  }

  off(event: string, callback: EventCallback): void {
    this.eventListeners.get(event)?.delete(callback);
  }

  private emit(event: string, data: any): void {
    const listeners = this.eventListeners.get(event);
    if (!listeners) return;
    for (const callback of listeners) {
      try {
        callback(data);
      } catch (error) {
        console.error(`[phoneService] Listener for ${event} threw:`, error);
      }
    }
  }

  // ─── Accessors ────────────────────────────────────────────────────────────
  isConnected(): boolean {
    return !!this.lockId;
  }

  getSessionId(): string | null {
    return this.sessionId;
  }

  getDeviceId(): string | null {
    return this.deviceId;
  }

  getLockId(): string | null {
    return this.lockId;
  }

  // ─── Internals ────────────────────────────────────────────────────────────

  /**
   * Subscribe to an execution doc's log stream. Phase 1 only uses this for
   * long-running flows — currently none in TLAM — but kept here because
   * Phase 2 (CLCC state polling) will need it. Returns an unsubscribe fn.
   *
   * @internal — retained for phase 2 use
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-private-class-members
  private subscribeToExecution(executionId: string, onLog: (entry: any) => void): Unsubscribe {
    if (this.activeExecUnsub) {
      this.activeExecUnsub();
      this.activeExecUnsub = null;
    }
    const unsub = onSnapshot(doc(firestoreDb, 'executions', executionId), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      (data.logs || []).forEach(onLog);
    });
    this.activeExecUnsub = unsub;
    return unsub;
  }
}

const phoneService = new PhoneService();
export default phoneService;
